import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  MenuItem,
  Select,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";

enum TimeRange {
  Week = "Week",
  Month = "Month",
  Year = "Year",
}

// Fetch functions
const fetchNewLeadsTotalCount = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/newLeadsTotalCount`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const fetchContactedLeads = async (
  range: TimeRange,
  token: string | null | undefined
) => {
  let endpoint = "";
  switch (range) {
    case TimeRange.Week:
      endpoint = "contactedLeadsForCurrentWeek";
      break;
    case TimeRange.Month:
      endpoint = "contactedLeadsForCurrentMonth";
      break;
    case TimeRange.Year:
      endpoint = "contactedLeadsForCurrentYear";
      break;
    default:
      endpoint = "contactedLeadsForCurrentWeek";
  }
  const { data } = await axios.get(`${apiVariable}/${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const fetchNotInterestedLeadsTotalCount = async (
  token: string | null | undefined
) => {
  const { data } = await axios.get(
    `${apiVariable}/notInterestedLeadsTotalCount`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

const fetchInterestedLeadsTotalCount = async (
  token: string | null | undefined
) => {
  const { data } = await axios.get(`${apiVariable}/interestedLeadsTotalCount`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const fetchNegotiatingLeadsTotalCount = async (
  token: string | null | undefined
) => {
  const { data } = await axios.get(
    `${apiVariable}/negotiatingLeadsTotalCount`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

const fetchClosedLeadsTotalCount = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/closedLeadsTotalCount`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const fetchProcessedLeadsTotalCount = async (
  token: string | null | undefined
) => {
  const { data } = await axios.get(`${apiVariable}/processedLeadsTotalCount`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const fetchTotalLeadsCount = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/totalLeadsCount`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const LeadStatisticsCard = () => {
  const { token } = useAuth();
  const [contactedRange, setContactedRange] = useState<TimeRange>(
    TimeRange.Week
  );

  const {
    data: newLeadsTotal,
    isLoading: isLoadingNewLeads,
    isError: isErrorNewLeads,
  } = useQuery({
    queryKey: ["newLeadsTotalCount"],
    queryFn: () => fetchNewLeadsTotalCount(token),
  });

  const {
    data: contactedLeads,
    isLoading: isLoadingContactedLeads,
    isError: isErrorContactedLeads,
  } = useQuery({
    queryKey: ["contactedLeads", contactedRange],
    queryFn: () => fetchContactedLeads(contactedRange, token),
  });

  const {
    data: notInterestedLeads,
    isLoading: isLoadingNotInterested,
    isError: isErrorNotInterested,
  } = useQuery({
    queryKey: ["notInterestedLeadsTotalCount"],
    queryFn: () => fetchNotInterestedLeadsTotalCount(token),
  });

  const {
    data: interestedLeads,
    isLoading: isLoadingInterested,
    isError: isErrorInterested,
  } = useQuery({
    queryKey: ["interestedLeadsTotalCount"],
    queryFn: () => fetchInterestedLeadsTotalCount(token),
  });

  const {
    data: negotiatingLeads,
    isLoading: isLoadingNegotiating,
    isError: isErrorNegotiating,
  } = useQuery({
    queryKey: ["negotiatingLeadsTotalCount"],
    queryFn: () => fetchNegotiatingLeadsTotalCount(token),
  });

  const {
    data: closedLeads,
    isLoading: isLoadingClosed,
    isError: isErrorClosed,
  } = useQuery({
    queryKey: ["closedLeadsTotalCount"],
    queryFn: () => fetchClosedLeadsTotalCount(token),
  });

  const {
    data: processedLeads,
    isLoading: isLoadingProcessed,
    isError: isErrorProcessed,
  } = useQuery({
    queryKey: ["processedLeadsTotalCount"],
    queryFn: () => fetchProcessedLeadsTotalCount(token),
  });

  const {
    data: totalLeads,
    isLoading: isLoadingTotalLeads,
    isError: isErrorTotalLeads,
  } = useQuery({
    queryKey: ["totalLeadsCount"],
    queryFn: () => fetchTotalLeadsCount(token),
  });

  const renderCount = (
    isLoading: boolean,
    isError: boolean,
    data: number | undefined
  ) => {
    if (isLoading) return <Typography>Loading...</Typography>;
    if (isError)
      return <Typography color="error">Error loading data</Typography>;
    if (data === undefined) return <Typography>No data</Typography>;
    return (
      <Typography variant="h4" color="primary">
        {data}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Grid container spacing={4} maxWidth="lg">
        {/* Row 1 */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <CardHeader
              title="New Leads (Total)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {renderCount(isLoadingNewLeads, isErrorNewLeads, newLeadsTotal)}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <CardHeader
              title="Contacted Leads"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={contactedRange}
                    onChange={(e) =>
                      setContactedRange(e.target.value as TimeRange)
                    }
                  >
                    <MenuItem value={TimeRange.Week}>1 Week</MenuItem>
                    <MenuItem value={TimeRange.Month}>1 Month</MenuItem>
                    <MenuItem value={TimeRange.Year}>1 Year</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent>
              {renderCount(
                isLoadingContactedLeads,
                isErrorContactedLeads,
                contactedLeads
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <CardHeader
              title="Not Interested Leads (Total)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {renderCount(
                isLoadingNotInterested,
                isErrorNotInterested,
                notInterestedLeads
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <CardHeader
              title="Interested Leads (Total)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {renderCount(
                isLoadingInterested,
                isErrorInterested,
                interestedLeads
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <CardHeader
              title="Negotiating Leads (Total)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {renderCount(
                isLoadingNegotiating,
                isErrorNegotiating,
                negotiatingLeads
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <CardHeader
              title="Closed Leads (Total)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {renderCount(isLoadingClosed, isErrorClosed, closedLeads)}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 3 */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <CardHeader
              title="Processed Leads (Total)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {renderCount(
                isLoadingProcessed,
                isErrorProcessed,
                processedLeads
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <CardHeader
              title="Total Leads (All)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {renderCount(isLoadingTotalLeads, isErrorTotalLeads, totalLeads)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LeadStatisticsCard;
